import { openCartDrawerGlobal, refreshCartDrawer } from "./cartDrawer";

/**
 * Shopify standard storefront actions (Shopify.actions)
 *
 * Apps and browser agents (WebMCP add_to_cart / update_cart_lines) change the cart
 * through Shopify.actions.updateCart. Without a configuration Shopify can't recognize
 * our cart drawer and falls back to a full page reload.
 *
 * Only the first configure() call per action wins, so the call is triggered from an
 * inline DOMContentLoaded listener above content_for_header (snippets/standard-actions-config.liquid)
 * that runs before any app listeners.
 *
 * Docs: https://shopify.dev/docs/api/storefront-events-and-actions/actions/configure
 */

type UpdateCartLine = { merchandiseId?: string; id?: string; quantity?: number };
type UpdateCartPayload = { lines?: UpdateCartLine[] };
type UpdateCartResult = { userErrors?: unknown[]; [key: string]: unknown };

type ConfigurableAction = {
  configure: (config: {
    eventTarget?: () => EventTarget | null;
    handler: (defaultHandler: () => Promise<any>, ...args: any[]) => Promise<any> | void;
  }) => boolean;
};

declare global {
  interface Window {
    ekConfigureStandardActions?: () => void;
  }
}

// A line with merchandiseId is an add (see updateCart docs)
const addsLines = (payload?: UpdateCartPayload) => Boolean(payload?.lines?.some((line) => line.merchandiseId));

export function configureStandardActions() {
  const actions = (window as any).Shopify?.actions as { updateCart?: ConfigurableAction; openCart?: ConfigurableAction } | undefined;
  if (!actions?.updateCart || !actions.openCart) return;

  actions.updateCart.configure({
    // We don't listen for shopify:cart:* events, so they dispatch from document
    eventTarget: () => null,
    async handler(defaultHandler, payload?: UpdateCartPayload) {
      const result: UpdateCartResult = await defaultHandler();
      if (result?.userErrors?.length) return result;

      if (addsLines(payload)) {
        // openCartDrawer refreshes the contents itself (or just refreshes when already open)
        openCartDrawerGlobal();
      } else {
        await refreshCartDrawer();
      }
      return result;
    },
  });

  actions.openCart.configure({
    handler() {
      openCartDrawerGlobal();
    },
  });
}

window.ekConfigureStandardActions = configureStandardActions;
